import type { Session } from "./session";

/** Why a signed-in person cannot use the app: identity-service's four callback refusals, or a token naming no tenant and no role. */
export type NoAccessReason = "not_enrolled" | "deactivated" | "tenant_deactivated" | "unresolved" | "no_membership";

export const noAccessCopy: Record<NoAccessReason, string> = {
  not_enrolled: "Your sign-in worked, but no account is registered for you here.",
  deactivated: "Your account has been suspended.",
  tenant_deactivated: "Your tenant has been suspended.",
  unresolved: "Your account could not be matched to a registered user.",
  no_membership: "Your account is registered but belongs to no tenant yet.",
};

// react-oidc-context wraps the callback's ErrorResponse as { name, message, innerError }; the envelope's
// error and error_description fields are on the inner error, or on the value itself when it is unwrapped.
export function noAccessReasonFromAuthError(error: unknown): NoAccessReason | undefined {
  if (!isRecord(error)) return undefined;
  const envelope = isRecord(error.innerError) ? error.innerError : error;
  if (envelope.error !== "access_denied") return undefined;
  const description = typeof envelope.error_description === "string" ? envelope.error_description.toLowerCase() : "";
  if (description.includes("not enrolled")) return "not_enrolled";
  if (description.includes("tenant is deactivated")) return "tenant_deactivated";
  if (description.includes("deactivated")) return "deactivated";
  return "unresolved";
}

/** A registered person the identity v2 token places in no tenant and grants no role: nothing in the app is open to them. */
export function sessionLacksAccess(session: Session): boolean {
  return (
    session.mode === "identity-v2" &&
    !session.isLoading &&
    session.tenantId === undefined &&
    session.platformRoles.length === 0 &&
    session.tenantRoles.length === 0
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
